import * as React from 'react'
import { CastingBase } from '../../../../services/json-schemas/base/casting'
import { QuotationBase } from '../../../../services/json-schemas/base/quotation'
import { currencyFormat, currencyWrapTaxNotice } from '../../../../services/utils/currency.service'
import PriceInputComponent from '../../offered-service/price/price-input.component'
import { I18nService } from '../../../../services/utils/i18n.service'
import { MarketplaceBase } from '../../../../services/json-schemas/base/marketplace'
import { OfferedServiceBase } from '../../../../services/json-schemas/base/offered_service'
import QuotationUseSpecificIncreasesCheckboxComponent from './quotation-use-specific-increases-checkbox.component'
import QuotationDescriptionInputComponent from './quotation-description-input.component'
import { SpecificIncreaseBase } from '../../../../services/json-schemas/base/specific_increase'

interface Props {
  castingMarket: Pick<MarketplaceBase, 'currency'>
  casting: Pick<CastingBase, 'rentable_unit_of_work'> & { price_of_options_adding_price: { price_cents: number, tax_cents: number } }
  offeredService: Pick<OfferedServiceBase, 'specific_pricing_mode' | 'description_plain_text'> & { specific_increases: Pick<SpecificIncreaseBase, 'id' | 'name' | 'percentage'>[] }
  quotation: Pick<QuotationBase, 'price_cents' | 'vat_price_cents' | 'round_on_vat_price' | 'description' | 'use_specific_increases'>
  onChange: React.Dispatch<React.SetStateAction<Partial<Props['quotation']>>>
  taxRate: number
  isNoVat: boolean
}

const i18nScoped = new I18nService(require('./quotation-form-unit-of-work-hour-day-month-event.component.yml'))

export default function QuotationFormUnitOfWorkHourDayMonthEventComponent ({ castingMarket, casting, offeredService, isNoVat, quotation, taxRate, onChange }: Props) {
  const computePricesAndEmit = React.useCallback((changes: Partial<Props['quotation']>) => {
    const body = { ...quotation, ...changes }

    const taxMulCoeff = isNoVat ? 1 : (1 + taxRate / 100)
    if (body.round_on_vat_price) body.price_cents = body.vat_price_cents === null ? null : body.vat_price_cents / taxMulCoeff
    else body.vat_price_cents = body.price_cents === null ? null : body.price_cents * taxMulCoeff

    if (body.price_cents) body.price_cents = Math.round(body.price_cents)
    if (body.vat_price_cents) body.vat_price_cents = Math.round(body.vat_price_cents)
    onChange(body)
  }, [isNoVat, onChange, quotation, taxRate])

  const growStyle = React.useCallback((weight: number) => {
    return { flexGrow: weight, flexBasis: 1, minWidth: (weight * 6) + 'em', margin: '0.5em' }
  }, [])

  const optionPrices = casting.price_of_options_adding_price
  const pricePlaceHolder = i18nScoped.t(`casting_custom_price${casting.rentable_unit_of_work === 'day' ? '_daily' : ''}${casting.rentable_unit_of_work === 'month' ? '_monthly' : ''}${quotation.round_on_vat_price ? '' : '_no_tax'}`)

  return <>
    <div className="u-mbm u-mtn boxPod" style={{ display: 'flex', flexWrap: 'wrap', padding: '1em' }}>
      <div style={ growStyle(1) }>
        <label>{ i18nScoped.t('description') }</label>
        <p style={{ marginTop: '1.2em' }}>{ i18nScoped.t('description_for_service_time') }</p>
      </div>

      <div style={ growStyle(1) }>
        <label>{ i18nScoped.t('quantity') }</label>
        <p style={{ marginTop: '1.2em' }}>{ i18nScoped.t(casting.rentable_unit_of_work === 'month' ? 'one_per_month' : 'computed_from_planning') }</p>
      </div>

      <PriceInputComponent
        currency={ castingMarket.currency }
        formName="quotation-"
        isNoVat={ isNoVat }
        fieldName="price_cents"
        fieldTranslator={ () => pricePlaceHolder }
        object={ quotation }
        style={ growStyle(2) }
        onChange={ val => computePricesAndEmit(val) } />

      <QuotationDescriptionInputComponent
        description={ quotation.description }
        offeredServiceDescriptionPlainText={ offeredService.description_plain_text }
        placeholder={ i18nScoped.t('description_placeholder') }
        onChange={ description => onChange({ description }) } />

      { casting.rentable_unit_of_work !== 'month' && <QuotationUseSpecificIncreasesCheckboxComponent
        checked={ quotation.use_specific_increases }
        offeredService={ offeredService }
        onChange={ checked => onChange({ use_specific_increases: checked }) } /> }
    </div>

    { /** The current implementation does not allow to add offered_options when rentable_unit_of_work === 'month' */ }
    { casting.rentable_unit_of_work !== 'month' && <div className="u-mbm u-mtn boxPod" style={{ display: 'flex', flexWrap: 'wrap', padding: '1em' }}>
      <div style={ growStyle(2) }>
        <label>{ i18nScoped.t('description') }</label>
        <p style={{ marginTop: '1.2em' }}>{ i18nScoped.t('options_selected_by_customer') }</p>
      </div>

      <div style={ growStyle(2) }>
        <label>{ i18nScoped.t('price_and_quantity') }</label>

        <p style={{ marginTop: '1.2em' }}>
          { +optionPrices.price_cents > 0 && i18nScoped.t(
            'option_price_adds_amount_on_each_event', {
              price_wihout_tax: currencyWrapTaxNotice(currencyFormat(castingMarket.currency, (+optionPrices.price_cents / 100).toFixed(2)), false),
              price_with_tax: currencyWrapTaxNotice(currencyFormat(castingMarket.currency, ((+optionPrices.price_cents + +optionPrices.tax_cents) / 100).toFixed(2)), true)
            }
          ) }

          { optionPrices.price_cents === 0 && i18nScoped.t('no_option_selected_by_customer') }
        </p>
      </div>
    </div> }
  </>
}
